/**
 * Same-machine lock signal (S03). vikod reports the OS screen-lock state and system idle to the
 * browser on the same computer, only as an encrypted `machine` report (never plaintext). The
 * browser may LOCK on it; nothing in it can unlock Viko.
 *
 * passkey-budget WP1 (lock follows the OS): unlocking without a passkey uses a separate,
 * daemon-SIGNED proof (`presence.unlock_proof`, `@viko/crypto` presence-unlock.ts) that the OS
 * screen unlocked after it locked, bound to a browser nonce and device. Only the app lock.
 */
import { z } from 'zod';
const Ts = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
/** System idle (no keyboard/mouse input anywhere on the machine) that counts as "away". */
export const SYSTEM_IDLE_LOCK_MS = 30 * 60_000;
export const MachinePresenceFrame = z.strictObject({
    type: z.literal('machine.presence'),
    ts: Ts,
    screen: z.enum(['locked', 'unlocked', 'unknown']),
    /** Most recent time the OS screen locked (kept after unlock so a missed lock still counts). */
    lockedAt: Ts.optional(),
    /** Most recent time system input idle reached SYSTEM_IDLE_LOCK_MS. */
    idleAt: Ts.optional(),
});
/**
 * passkey-budget WP1: `presence.unlock_proof` asks this machine's vikod to sign that its OS screen
 * locked at or after `since` and then unlocked. Non-risky (it only clears the app lock of the
 * requesting device); the result is a `PresenceUnlockProof` the browser verifies with its pin.
 */
export const PRESENCE_COMMAND_PAYLOADS = {
    'presence.unlock_proof': z.strictObject({
        /** Browser-chosen 32-byte nonce, base64url. */
        nonce: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
        since: Ts,
    }),
};
//# sourceMappingURL=machine-presence.js.map